import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import CarrierLayout from './layouts/CarrierLayout';
import ShipperLayout from './layouts/ShipperLayout';
import BrokerLayout from './layouts/BrokerLayout';
import { ConsentProvider } from './contexts/ConsentContext';
import ConsentBanner from './components/common/ConsentBanner';
import { ErrorBoundaryProvider } from './components/ErrorBoundaryProvider';
import PerformanceMonitor from './components/performance/PerformanceMonitor';
import DatabasePerformanceDashboard from './components/database/DatabasePerformanceDashboard';
import DataStructureDashboard from './components/database/DataStructureDashboard';
import IndexPerformanceDashboard from './components/database/IndexPerformanceDashboard';
import FailoverMonitor from './components/failover/FailoverMonitor';
import DataProtectionDashboard from './components/security/DataProtectionDashboard';
import SecurityHeadersDashboard from './components/security/SecurityHeadersDashboard';
import SecurityIncidentDashboard from './components/security/SecurityIncidentDashboard';
// Security testing dashboard - only in development
// import SecurityTestingDashboard from './components/security/SecurityTestingDashboard';
import SOC2ComplianceDashboard from './components/security/SOC2ComplianceDashboard';
import securityHeadersService from './services/securityHeadersService';
import cspService from './services/cspService';
import corsService from './services/corsService';
import securityIncidentResponse from './services/securityIncidentResponse';
import securityThreatIntelligence from './services/securityThreatIntelligence';
import automatedSecurityResponse from './services/automatedSecurityResponse';
// Automated security testing - only in development
// import automatedSecurityTesting from './services/automatedSecurityTesting';
import vulnerabilityScanningEngine from './services/vulnerabilityScanningEngine';
import enhancedDataClassificationService from './services/enhancedDataClassificationService';
import Logger from './services/logger';
import { LOCAL_DEV_CORS_ORIGINS } from './constants/localDevOrigins';
import privacyManagementService from './services/privacyManagementService';
// Import location test utility (exposes window.testLocation functions)
import './utils/locationTest';
import confidentialityMonitoringService from './services/confidentialityMonitoringService';
import uptimeSLAMonitoringService from './services/uptimeSLAMonitoringService';
import dataIntegrityMonitoringService from './services/dataIntegrityMonitoringService';

// Import lazy-loaded components
import {
  // Auth Routes
  LazyLogin,
  LazyRegister,
  
  // Carrier Routes
  LazyCarrierDashboard,
  LazyAvailableLoads,
  LazyMyLoads,
  LazyLoadDetails,
  LazyHomeFeed,
  LazyCarrierProfile,
  LazyCarrierMyProfile,
  LazyCarrierPayments,
  LazyCarrierPartners,
  LazyCarrierSettings,
  LazyOnboardingWalkthrough,
  LazyBrokerOnboarding,
  LazyShipperOnboarding,
  LazyBrokerDetails,
  LazyShipperDetails,
  LazyBrokerProfileSetup,
  LazyBrokerProfileView,
  LazyShipperProfileSetup,
  LazyShipperProfileView,
  
  // Shipper Routes
  LazyShipperPages,
  LazyShipperDashboard,
  LazyPurchaseOrders,
  LazyTestPurchaseOrder,
  LazyCreateShipment,
  LazyShipperCarrierPartners,
  LazyShippingSchedule,
  LazyShipperProfile,
  LazyShipperSettings,
  
  // Broker Routes
  LazyBrokerDashboard,
  LazyBrokerPurchaseOrders,
  LazyBrokerCreatePO,
  LazyBrokerCarrierPartners,
  LazyBrokerCarrierDetails,
  LazyBrokerShippingSchedule,
  LazyBrokerDriverUpdates,
  LazyBrokerPayInvoices,
  LazyBrokerShipmentArchive,
  LazyBrokerCarrierDirectory,
  LazyBrokerSettings,
  LazyBrokerProfile,
  LazyBrokerMultiLoadCarrierAssignment,
  
  // Admin Routes
  LazyAdminDashboard,
  LazyAdminSetup,
  LazyAdminManagement,
  LazyAdminAccess,
  LazyAdminToolsHub,
  LazyMonitoringDashboard,
  LazyFeatureFlagManager,
  LazyCacheManager,
  LazyRateLimitManager,
  LazySubscriptionTierManager,
  LazySuperAdminAccountCancellation,
  LazyAuthorityContinuityIntelligenceDashboard,
  LazyFailoverStatus,
  LazyAdvancedPerformanceDashboard,
  LazySystemHealthMonitor,
  LazyAvailabilityDashboard,
  LazyProcessingIntegrityDashboard,
  LazySecurityComplianceDashboard,
  LazyUnifiedSOC2ComplianceDashboard,
  
  // Public Routes
  LazyPublicPages,
  LazyLandingPage,
  LazyTechnologyPage,
  LazyAboutPage,
  LazyOAuthCallback,
  LazyViewProfile,
  LazyFeatureFlagTest
} from './components/lazy/LazyRoutes';

// Import non-lazy components (layouts, contexts, etc.)
import { AuthProvider } from './contexts/AuthContext';
import { CarrierProvider } from './context/CarrierContext';
import { ShipmentsProvider } from './context/ShipmentsContext';
import { LoadsProvider } from './context/LoadsContext';
import { LocationProvider, useLocationContext } from './contexts/LocationContext';
import AdminRoute from './components/common/AdminRoute';
import './utils/quickAdminSetup';

// Import auth components directly to avoid lazy loading issues
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ContinueRegistration from './pages/auth/ContinueRegistration';
import ConfirmCancellation from './pages/ConfirmCancellation';
import ConfirmTeamRemoval from './pages/ConfirmTeamRemoval';
import {
  ADMIN_CANCELLATION_UI,
  CANCELLATION_CONFIRM_UI,
  TEAM_REMOVAL_CONFIRM_UI,
} from './constants/accountCancellationWorkflow';
import { ACI_ADMIN_UI_ROUTE_PATH } from './constants/authorityContinuityIntelligence';
import ChangeSecurityPhrase from './pages/auth/ChangeSecurityPhrase';

// Import landing page directly to avoid lazy loading delay (fixes flash issue)
import LandingPage from './pages/LandingPage';

// Import additional components that need to be loaded immediately
import NewLoad from './pages/shipper/NewLoad';
import ViewPeople from './pages/shipper/ViewPeople';
import ActiveSearches from './pages/shipper/ActiveSearches';
import InProgress from './pages/shipper/InProgress';
import Completed from './pages/shipper/Completed';
import DriverUpdates from './pages/shipper/DriverUpdates';

const ViewProfileWrapper = () => {
  const { id } = useParams();
  return <LazyViewProfile id={id || ''} />;
};

// Simple location notification component - FIXED: Uses LocationContext to avoid duplicate location requests
function LocationNotification() {
  const [showNotification, setShowNotification] = useState(false);
  // FIXED: Use LocationContext instead of making duplicate geolocation requests
  // This prevents conflicts with the centralized location handling
  const { location, error, permissionState } = useLocationContext();

  useEffect(() => {
    // Only show notification if there's a meaningful state change
    if (location) {
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } else if (error || permissionState === 'denied') {
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);
    }
  }, [location, error, permissionState]);

  if (!showNotification) return null;

  const locationStatus = location ? 'granted' : (permissionState === 'denied' || error ? 'denied' : 'not supported');

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      backgroundColor: locationStatus === 'granted' ? '#4CAF50' : '#f44336',
      color: 'white',
      borderRadius: '4px',
      zIndex: 1000,
      fontSize: '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    }}>
      {locationStatus === 'granted' 
        ? '✓ Location access granted' 
        : locationStatus === 'denied' 
        ? '⚠ Location access denied - some features may not work' 
        : '⚠ Location not supported by this browser'
      }
    </div>
  );
}

function App() {
  // Initialize security services
  useEffect(() => {
    try {
      // Initialize security headers service
      securityHeadersService.initialize({
        enableHSTS: true,
        enableXSSProtection: true,
        enableContentTypeOptions: true,
        enableFrameOptions: true,
        enableReferrerPolicy: true,
        enablePermissionsPolicy: true,
        enableCSP: true,
        hstsMaxAge: 31536000, // 1 year
        hstsIncludeSubDomains: true,
        hstsPreload: true,
        frameOptions: 'SAMEORIGIN',
        referrerPolicy: 'strict-origin-when-cross-origin',
        cspReportOnly: false
      });

      // Initialize CSP service
      cspService.initialize({
        mode: 'enforce',
        enableNonce: false,
        enableStrictDynamic: false,
        enableUnsafeInline: true,
        enableUnsafeEval: true,
        environment: process.env.NODE_ENV === 'production' ? 'production' : 'development'
      });

             // Initialize CORS service
             corsService.initialize({
               allowedOrigins: [
                 'https://tranzit.io',
                 'https://www.tranzit.io',
                 'https://app.tranzit.io',
                 'https://admin.tranzit.io',
                 'https://staging.tranzit.io',
                 ...(process.env.NODE_ENV !== 'production' ? [...LOCAL_DEV_CORS_ORIGINS] : []),
               ],
               allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'],
               allowedHeaders: [
                 'Content-Type',
                 'Authorization',
                 'X-Requested-With',
                 'Accept',
                 'Origin',
                 'Access-Control-Request-Method',
                 'Access-Control-Request-Headers',
                 'X-API-Key',
                 'X-User-ID',
                 'X-Session-ID'
               ],
               allowCredentials: true,
               maxAge: 86400,
               enablePreflight: true,
               strictMode: false,
               environment: process.env.NODE_ENV === 'production' ? 'production' : 'development'
             });

             // Initialize Security Incident Response
             securityIncidentResponse.addNotificationListener('app', (incident) => {
               Logger.debug('Security incident detected:', { incident });
             });

             // Initialize Security Threat Intelligence
             securityThreatIntelligence.addThreatIndicator({
               type: 'ip_address',
               value: '192.168.1.1',
               confidence: 85,
               severity: 'medium',
               source: 'internal',
               frequency: 1,
               context: {
                 attackVector: 'network',
                 targetSystem: 'web_server',
                 attackType: 'scanning',
                 timePattern: {
                   hour: new Date().getHours(),
                   dayOfWeek: new Date().getDay(),
                   month: new Date().getMonth()
                 }
               },
               tags: ['scanning', 'suspicious'],
               verified: false,
               falsePositive: false
             });

             // Initialize Automated Security Response
             automatedSecurityResponse.processIncident = automatedSecurityResponse.processIncident.bind(automatedSecurityResponse);
             automatedSecurityResponse.processThreatAnalysis = automatedSecurityResponse.processThreatAnalysis.bind(automatedSecurityResponse);

             // Initialize SOC 2 Compliance Services
             enhancedDataClassificationService.initialize({
               enableAutoClassification: true,
               enableManualReview: true,
               enableConfidenceThreshold: true,
               confidenceThreshold: 80,
               enableAuditLogging: true,
               enableRealTimeMonitoring: true,
               enableComplianceReporting: true,
               enableDataLossPrevention: true,
               enableRetentionManagement: true,
               enableAccessControl: true,
               auditRetentionDays: 2555, // 7 years
               reportingFrequency: 'weekly',
               escalationEnabled: true,
               notificationChannels: ['email', 'dashboard', 'slack']
             });

             // Initialize Uptime SLA Monitoring
             uptimeSLAMonitoringService.updateConfig({
               enabled: true,
               targetUptime: 99.9,
               checkInterval: 60000 // 1 minute
             });

             privacyManagementService.initialize({
               enableConsentManagement: true,
               enableDataSubjectRights: true,
               enablePrivacyImpactAssessments: true,
               enableConsentWithdrawal: true,
               enableDataPortability: true,
               enableAuditLogging: true,
               enableNotifications: true,
               enableComplianceReporting: true,
               consentRetentionDays: 2555, // 7 years
               requestRetentionDays: 2555, // 7 years
               piaRetentionDays: 2555, // 7 years
               notificationChannels: ['email', 'dashboard'],
               responseTimeThresholds: {
                 access: 30,
                 rectification: 30,
                 erasure: 30,
                 portability: 30,
                 restriction: 30,
                 objection: 30,
                 withdraw_consent: 1
               },
               verificationRequired: true,
               feesEnabled: false,
               maxFees: 0
             });

             confidentialityMonitoringService.initialize({
               enableRealTimeMonitoring: true,
               enableBreachDetection: true,
               enableAccessControl: true,
               enableDataHandlingProcedures: true,
               enableAuditLogging: true,
               enableNotifications: true,
               enableComplianceReporting: true,
               riskThresholds: {
                 low: 25,
                 medium: 50,
                 high: 75,
                 critical: 90
               },
               responseTimeThresholds: {
                 low: 60, // 1 hour
                 medium: 30, // 30 minutes
                 high: 15, // 15 minutes
                 critical: 5 // 5 minutes
               },
               retentionDays: 2555, // 7 years
               notificationChannels: ['email', 'dashboard', 'slack'],
               escalationEnabled: true,
               autoResponseEnabled: true
             });

             // Initialize Security Testing Services (development only)
             if (process.env.NODE_ENV !== 'production') {
               // Only import and run in development - use async IIFE
               (async () => {
                 const { default: automatedSecurityTesting } = await import('./services/automatedSecurityTesting');
                 automatedSecurityTesting.runTestSuite('owasp_top_10');
                 vulnerabilityScanningEngine.queueScan({
                 id: `initial_scan_${Date.now()}`,
                 name: 'Initial Security Scan',
                 type: 'full',
                 status: 'pending',
                 target: {
                   type: 'application',
                   url: window.location.origin,
                   scope: ['/', '/api'],
                   exclusions: ['/static', '/assets']
                 },
                 configuration: {
                   scanDepth: 'medium',
                   scanSpeed: 'medium',
                   enableAuthentication: true,
                   enableBruteForce: false,
                   enableInjectionTests: true,
                   enableConfigurationTests: true,
                   enableDependencyTests: true,
                   enableNetworkTests: false,
                   timeout: 300000,
                   maxConcurrentRequests: 5,
                   retryCount: 3,
                   delayBetweenRequests: 1000
                 },
                 vulnerabilities: [], // Will be populated during scan
                 statistics: {
                   totalVulnerabilities: 0,
                   vulnerabilitiesBySeverity: {},
                   vulnerabilitiesByCategory: {},
                   falsePositiveRate: 0,
                   scanCoverage: 0,
                   averageResponseTime: 0,
                   totalRequests: 0,
                   successfulRequests: 0,
                   failedRequests: 0,
                   scanDuration: 0
                 }
               });
               })();
             }

           } catch (error) {
             Logger.error('Failed to initialize security services:', error instanceof Error ? error : new Error(String(error)), { error });
           }
         }, []);

  return (
    <HelmetProvider>
    <ErrorBoundaryProvider>
    <ConsentProvider>
      <AuthProvider>
        <CarrierProvider>
          <ShipmentsProvider>
            <LoadsProvider>
              <LocationProvider>
                <BrowserRouter>
                <Routes>
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  
                  {/* Landing Page - Direct import to avoid lazy loading delay (fixes flash issue) */}
                  <Route path="/" element={<LandingPage />} />
                  
                  {/* Carrier Routes */}
                  <Route path="/carrier" element={<CarrierLayout />}>
                    <Route index element={<LazyCarrierDashboard />} />
                    <Route path="dashboard" element={<LazyCarrierDashboard />} />
                    <Route path="home" element={<LazyHomeFeed />} />
                    <Route path="onboarding" element={<LazyOnboardingWalkthrough />} />
                    <Route path="available-loads" element={<LazyAvailableLoads />} />
                    <Route path="my-loads" element={<LazyMyLoads />} />
                    <Route path="load-details/:id" element={<LazyLoadDetails />} />
                  <Route path="loads/:id" element={<LazyLoadDetails />} />
                    <Route path="home-feed" element={<LazyHomeFeed />} />
                    <Route path="profile" element={<LazyCarrierProfile />} />
                    <Route path="my-profile" element={<LazyCarrierMyProfile />} />
                    <Route path="payments" element={<LazyCarrierPayments />} />
                    <Route path="partners" element={<LazyCarrierPartners />} />
                    <Route path="settings" element={<LazyCarrierSettings />} />
                    <Route path="broker-details" element={<LazyBrokerDetails />} />
                    <Route path="shipper-details" element={<LazyShipperDetails />} />
                  </Route>

                  {/* Shipper Routes */}
                  <Route path="/shipper" element={<ShipperLayout />}>
                    <Route path="onboarding" element={<LazyShipperOnboarding />} />
                    <Route path="profile-view" element={<LazyShipperProfileView />} />
                    <Route path="*" element={<LazyShipperPages />} />
                  </Route>

                  {/* Broker Routes */}
                   <Route path="/broker" element={<BrokerLayout />}>
                    <Route index element={<LazyBrokerDashboard />} />
                    <Route path="onboarding" element={<LazyBrokerOnboarding />} />
                    <Route path="dashboard" element={<LazyBrokerDashboard />} />
                    <Route path="driver-updates" element={<LazyBrokerDriverUpdates />} />
                    <Route path="shipping-schedule" element={<LazyBrokerShippingSchedule />} />
                    <Route path="purchase-orders" element={<LazyBrokerPurchaseOrders />} />
                    <Route path="create-po" element={<LazyBrokerCreatePO />} />
                    <Route path="carrier-partners" element={<LazyBrokerCarrierPartners />} />
                    <Route path="carrier-partners/:id" element={<LazyBrokerCarrierDetails />} />
                    <Route path="multi-load-assignment" element={<LazyBrokerMultiLoadCarrierAssignment />} />
                    <Route path="broker-shipping-schedule" element={<LazyBrokerShippingSchedule />} />
                    <Route path="pay-invoices" element={<LazyBrokerPayInvoices />} />
                    <Route path="shipment-archive" element={<LazyBrokerShipmentArchive />} />
                    <Route path="carrier-directory" element={<LazyBrokerCarrierDirectory />} />
                    <Route path="settings" element={<LazyBrokerSettings />} />
                    <Route path="profile" element={<LazyBrokerProfile />} />
                    <Route path="profile-setup" element={<LazyBrokerProfileSetup />} />
                    <Route path="profile-view" element={<LazyBrokerProfileView />} />
                   </Route>

                  {/* Auth Routes - Using direct imports to avoid lazy loading issues */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/continue-registration" element={<ContinueRegistration />} />
                  <Route path={CANCELLATION_CONFIRM_UI.ROUTE_PATH} element={<ConfirmCancellation />} />
                  <Route path={TEAM_REMOVAL_CONFIRM_UI.ROUTE_PATH} element={<ConfirmTeamRemoval />} />
                  <Route path="/change-security-phrase" element={<ChangeSecurityPhrase />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={
                    <AdminRoute>
                      <LazyAdminDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/setup" element={
                    <AdminRoute>
                      <LazyAdminSetup />
                    </AdminRoute>
                  } />
                  <Route path="/admin/management" element={
                    <AdminRoute>
                      <LazyAdminManagement />
                    </AdminRoute>
                  } />
                  <Route path="/admin/access" element={
                    <AdminRoute>
                      <LazyAdminAccess />
                    </AdminRoute>
                  } />
                  <Route path="/admin/tools" element={
                    <AdminRoute>
                      <LazyAdminToolsHub />
                    </AdminRoute>
                  } />
                  <Route path="/admin/monitoring" element={
                    <AdminRoute>
                      <LazyMonitoringDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/feature-flags" element={
                    <AdminRoute>
                      <LazyFeatureFlagManager />
                    </AdminRoute>
                  } />
                  <Route path="/test-feature-flags" element={<LazyFeatureFlagTest />} />
                  <Route path="/admin/cache" element={
                    <AdminRoute>
                      <LazyCacheManager />
                    </AdminRoute>
                  } />
                  <Route path="/admin/rate-limits" element={
                    <AdminRoute>
                      <LazyRateLimitManager />
                    </AdminRoute>
                  } />
                  <Route path="/admin/subscription-tiers" element={
                    <AdminRoute>
                      <LazySubscriptionTierManager />
                    </AdminRoute>
                  } />
                  <Route path={ADMIN_CANCELLATION_UI.ROUTE_PATH} element={
                    <AdminRoute>
                      <LazySuperAdminAccountCancellation />
                    </AdminRoute>
                  } />
                  <Route path={ACI_ADMIN_UI_ROUTE_PATH} element={
                    <AdminRoute>
                      <LazyAuthorityContinuityIntelligenceDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/failover" element={
                    <AdminRoute>
                      <LazyFailoverStatus />
                    </AdminRoute>
                  } />
                  <Route path="/subscription-tiers" element={<LazySubscriptionTierManager />} />
                  <Route path="/admin/advanced-performance" element={
                    <AdminRoute>
                      <LazyAdvancedPerformanceDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/system-health" element={
                    <AdminRoute>
                      <LazySystemHealthMonitor />
                    </AdminRoute>
                  } />

                  <Route path="/admin/availability" element={
                    <AdminRoute>
                      <LazyAvailabilityDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/integrity" element={
                    <AdminRoute>
                      <LazyProcessingIntegrityDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/security-compliance" element={
                    <AdminRoute>
                      <LazySecurityComplianceDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/compliance" element={
                    <AdminRoute>
                      <LazyUnifiedSOC2ComplianceDashboard />
                    </AdminRoute>
                  } />

                  {/* Public Routes - Must be last to catch all unmatched routes */}
                  <Route path="/*" element={<LazyPublicPages />} />
                </Routes>
                </BrowserRouter>
                {/* FIXED: LocationNotification moved inside LocationProvider to access context */}
                <LocationNotification />
              </LocationProvider>
            </LoadsProvider>
          </ShipmentsProvider>
        </CarrierProvider>
        <ConsentBanner />
                  {/* Development dashboards - disabled for cleaner UI */}
                  {/* <PerformanceMonitor showDetails={true} />
                  <DatabasePerformanceDashboard showDetails={true} />
                  <DataStructureDashboard showDetails={true} />
                  <IndexPerformanceDashboard showDetails={true} />
                  <FailoverMonitor showDetails={true} />
                  <DataProtectionDashboard showDetails={true} />
                  <SecurityHeadersDashboard showDetails={true} />
                  <SecurityIncidentDashboard showDetails={true} />
                  <SecurityTestingDashboard showDetails={true} />
                  <SOC2ComplianceDashboard showDetails={true} /> */}
      </AuthProvider>
    </ConsentProvider>
      </ErrorBoundaryProvider>
    </HelmetProvider>
  );
}

export default App;